import React, { ComponentProps, Ref, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Eye, NoEye, OutlinedError } from '../../icons'

interface InputProps extends ComponentProps<'input'> {
  title?: string
  isRequired?: boolean
  maxlength?: number
  error?: string
  subfield?: React.ReactNode
  isTextArea?: boolean
  textAreaClassName?: string
  iconButton?: React.ReactElement
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  inputRef?: Ref<HTMLInputElement>
  labelClassName?: string
}

const Input = ({
  id,
  title = '',
  name = '',
  placeholder = '',
  isRequired = false,
  type = 'text',
  value,
  maxlength = 50,
  onBlur,
  onInput,
  onChange,
  error,
  subfield,
  className,
  textAreaClassName = 'h-44',
  isTextArea,
  iconButton,
  inputRef,
  labelClassName,
}: InputProps): React.ReactElement => {
  const [inputType, setInputType] = useState<string | undefined>(type)
  const isPassword = type === 'password'
  const isPasswordHidden = inputType === 'password'
  const iconProps = {
    hardcodedColor: 'currentColor',
    className: 'text-gray-40 w-4',
    strokeWidth: '2',
  }
  const sharedProps = {
    id,
    name,
    placeholder,
    required: isRequired,
    value,
    onChange,
    className: `w-full rounded border bg-white p-2.5 text-base placeholder:text-gray-40 ${!error ? 'border-gray' : 'border-red-300 focus:border-red-400'} ${isTextArea ? textAreaClassName : ''} ${isPassword ? 'pr-10' : ''}`,
    maxLength: maxlength,
    'aria-invalid': !!error,
    'aria-describedby': error ? `${id}-error` : undefined,
  }

  const togglePasswordVisibility = () => {
    setInputType(isPasswordHidden ? 'text' : 'password')
  }

  return (
    <div className={twMerge(`md:mb-7.5 mb-7 w-full`, className)}>
      {title && (
        <label
          htmlFor={id}
          className={twMerge(
            'mb-2 block whitespace-nowrap text-sm font-semibold',
            labelClassName,
          )}
        >
          {title} {isRequired && '*'}
        </label>
      )}

      {isTextArea ? (
        <textarea {...sharedProps} />
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            type={inputType}
            onBlur={onBlur}
            onInput={onInput}
            {...sharedProps}
          />
          {iconButton ? (
            iconButton
          ) : isPassword ? (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2"
              onClick={togglePasswordVisibility}
            >
              {isPasswordHidden ? <Eye {...iconProps} /> : <NoEye {...iconProps} />}
            </button>
          ) : null}
        </div>
      )}
      {error && (
        <div
          id={`${id}-error`}
          role="alert"
          className="mt-2 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600"
        >
          <OutlinedError
            width={18}
            height={18}
            hardcodedColor="#DC2626"
            className="mt-0.5 shrink-0"
          />
          <span>{error}</span>
        </div>
      )}
      {subfield && <>{subfield}</>}
    </div>
  )
}

export default Input
